import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Expand } from "lucide-react";
import { Lightbox } from "./Lightbox";

const DELAY = 4000;

export const AboutCarousel = ({ photos = [] }) => {
  const list = Array.isArray(photos) ? photos.filter((p) => p?.img) : [];
  const total = list.length;
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const [paused, setPaused] = useState(false);
  const [open, setOpen] = useState(null);

  const go = useCallback((d) => {
    setDir(d);
    setI((x) => (x + d + total) % total);
  }, [total]);

  useEffect(() => {
    if (total < 2 || paused || open !== null) return undefined;
    const t = setInterval(() => go(1), DELAY);
    return () => clearInterval(t);
  }, [total, paused, open, go, i]);

  if (!total) return null;
  const cur = list[i % total];

  return (
    <div data-testid="nosotros-carousel" className="relative mx-auto mt-10 max-w-xl lg:mt-14 lg:max-w-none" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[2rem] border border-gold/25 bg-espresso">
        <AnimatePresence initial={false} custom={dir}>
          <motion.button
            key={i}
            type="button"
            data-testid="nosotros-carousel-slide"
            custom={dir}
            variants={{
              enter: (d) => ({ x: d > 0 ? "100%" : "-100%", opacity: 0.4 }),
              center: { x: 0, opacity: 1 },
              exit: (d) => ({ x: d > 0 ? "-30%" : "30%", opacity: 0 }),
            }}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.3}
            onDragEnd={(_, info) => {
              if (info.offset.x < -60) go(1);
              else if (info.offset.x > 60) go(-1);
            }}
            onClick={() => setOpen(i)}
            aria-label={`Ver foto: ${cur.cap || "Café Amadeus"}`}
            className="absolute inset-0 cursor-zoom-in"
          >
            <motion.img
              src={cur.img}
              alt={cur.cap || "Café Amadeus"}
              draggable={false}
              initial={{ scale: 1.12 }}
              animate={{ scale: 1 }}
              transition={{ duration: DELAY / 1000 + 1, ease: "linear" }}
              className="h-full w-full object-cover"
            />
            <span className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-espresso/95 via-espresso/50 to-transparent" />
          </motion.button>
        </AnimatePresence>

        {cur.cap && (
          <p data-testid="nosotros-carousel-caption" className="pointer-events-none absolute bottom-5 left-5 right-20 font-display text-xl italic text-cream md:text-2xl">{cur.cap}</p>
        )}
        <span className="pointer-events-none absolute right-4 top-4 rounded-full border border-gold/40 bg-espresso/60 p-2.5 text-cream backdrop-blur-md"><Expand size={16} /></span>
      </div>

      {total > 1 && (
        <div className="mt-5 flex items-center justify-between">
          <div className="flex gap-2">
            {list.map((_, k) => (
              <button
                key={k}
                type="button"
                data-testid={`nosotros-carousel-dot-${k}`}
                aria-label={`Foto ${k + 1}`}
                onClick={() => { setDir(k > i ? 1 : -1); setI(k); }}
                className="relative h-1.5 w-5 overflow-hidden rounded-full bg-gold/20 sm:w-8"
              >
                {k === i && (
                  <motion.span
                    key={`${i}-${paused}`}
                    initial={{ width: paused ? "100%" : "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: paused ? 0 : DELAY / 1000, ease: "linear" }}
                    className="absolute inset-y-0 left-0 bg-gold"
                  />
                )}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button type="button" data-testid="nosotros-carousel-prev" aria-label="Anterior" onClick={() => go(-1)} className="rounded-full border border-gold/40 p-3 text-cream transition-colors hover:bg-gold hover:text-espresso"><ChevronLeft size={18} /></button>
            <button type="button" data-testid="nosotros-carousel-next" aria-label="Siguiente" onClick={() => go(1)} className="rounded-full border border-gold/40 p-3 text-cream transition-colors hover:bg-gold hover:text-espresso"><ChevronRight size={18} /></button>
          </div>
        </div>
      )}

      <Lightbox images={list.map((p) => ({ src: p.img, cap: p.cap }))} index={open} onChange={setOpen} onClose={() => setOpen(null)} />
    </div>
  );
};
